import React, { useEffect, useState } from "react";
import { ArrowRight, Play, ChevronDown, Construction } from "lucide-react";
import { brand, heroImage, heroStats, currentProject } from "../../mock";
import { scrollToId } from "./Header";

export const CountUp = ({ to, duration = 1600 }) => {
  const [n, setN] = useState(0);
  const ref = React.useRef(null);
  useEffect(() => {
    let started = false;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started) {
        started = true;
        const t0 = performance.now();
        const tick = (t) => {
          const p = Math.min((t - t0) / duration, 1);
          setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{n}</span>;
};

export default function Hero({ onQuote }) {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const f = () => setOffset(window.scrollY * 0.35);
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);

  return (
    <section id="home" className="relative min-h-screen pt-20 overflow-hidden grain">
      <div
        className="absolute inset-0 bg-cover bg-center scale-110"
        style={{ backgroundImage: `url(${heroImage})`, transform: `translateY(${offset}px) scale(1.1)` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/50" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black to-transparent" />

      <div className="relative max-w-[1260px] mx-auto px-6 lg:px-10 pt-12 pb-24 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
        <div className="reveal in">
          <span className="inline-flex items-center gap-2 border border-white/25 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 text-yellow-400 text-[13px] tracking-[0.15em] font-medium">
            <span className="w-2 h-2 rounded-full bg-yellow-400 pulse-dot" /> {brand.est}
          </span>
          <h1 className="font-display font-bold text-white uppercase leading-[0.92] tracking-[-0.04em] text-[52px] sm:text-[72px] lg:text-[92px] mt-7">
            Building the<br />Future Today
          </h1>
          <p className="mt-7 text-white/75 text-lg lg:text-[19px] max-w-[440px] leading-relaxed">
            Premium construction delivered with precision, integrity and craftsmanship you can see in every detail.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <button
              onClick={onQuote}
              data-testid="hero-start-project"
              className="group flex items-center gap-3 bg-yellow-400 text-black font-semibold text-[17px] px-10 py-4 rounded-2xl hover:bg-yellow-300 hover:shadow-[0_10px_40px_-10px_rgba(250,204,21,0.7)] transition-[background-color,box-shadow] duration-300"
            >
              START A PROJECT <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => scrollToId("projects")}
              data-testid="hero-view-work"
              className="flex items-center gap-2 border border-white/40 text-white text-[16px] px-8 py-4 rounded-2xl hover:bg-white/10 hover:border-white transition-colors duration-300"
            >
              <Play className="w-3.5 h-3.5 fill-white" /> VIEW WORK
            </button>
          </div>
          <div className="mt-14 flex flex-wrap gap-10 lg:gap-12">
            {heroStats.map((s) => (
              <div key={s.label}>
                <div className="font-display font-bold text-yellow-400 text-[36px] leading-none"><CountUp to={s.value} /></div>
                <div className="text-white/60 text-[12px] tracking-[0.15em] uppercase mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative hidden lg:block w-[400px] h-[380px]">
          <div className="float-slow absolute top-0 left-0 w-[260px] rounded-2xl bg-white/[0.07] border border-white/15 backdrop-blur-xl p-5 shadow-2xl">
            <div className="flex -space-x-2">
              {["bg-zinc-600", "bg-yellow-400", "bg-sky-500"].map((c, i) => (
                <span key={i} className={`w-9 h-9 rounded-full ${c} border-2 border-zinc-900 flex items-center justify-center text-[11px] font-bold text-white`}>
                  {i === 2 ? "AL" : ""}
                </span>
              ))}
            </div>
            <p className="text-white/70 text-[12.5px] mt-4">Trusted by the region's leading developers</p>
          </div>
          <div className="float-slower absolute top-[135px] right-0 w-[270px] rounded-2xl bg-zinc-900/90 border border-yellow-400/30 backdrop-blur-xl p-5 shadow-2xl flex gap-4 items-center">
            <Construction className="w-14 h-14 text-orange-400 shrink-0" strokeWidth={1.4} />
            <div className="flex-1">
              <div className="text-yellow-400 text-[13px] uppercase tracking-wide">Current Project</div>
              <div className="text-white font-bold text-[20px] leading-tight mt-1">{currentProject.title}</div>
              <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-yellow-400 rounded-full transition-[width] duration-1000" style={{ width: `${currentProject.progress}%` }} />
              </div>
              <div className="flex justify-between text-white/60 text-[11px] mt-2">
                <span>{currentProject.progress}% COMPLETE</span><span>{currentProject.due}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button onClick={() => scrollToId("services")} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/50 hover:text-white text-[11px] tracking-[0.2em] transition-colors">
        SCROLL TO EXPLORE <ChevronDown className="w-4 h-4 text-yellow-400 bounce-y" />
      </button>
    </section>
  );
}
